import type { GameState, VisitLogEntry, Dex, Island } from './state';
import { SAVE_VERSION, INITIAL_SEED, VISIT_LOG_LIMIT, MAX_ISLANDS, createInitialState, createEmptyDex } from './state';
import type { GardenCell, GardenGrid, Orientation } from './garden';
import { GRID_SIZE, BLOCKED_CELLS, createEmptyGarden, isValidCell, isCellInShape, shapeForIslandIndex, ensureEntrance, ENTRANCE_INDEX } from './garden';
import type { IslandShape } from './garden';
import { getDeco, clampLevel, totalInvested, ENTRANCE_DECO_ID } from './decos';
import { CUSTOMER_CATALOG } from './customers';
import type { Planting, TreeForm } from './plants';
import { isPlantDeco, createPlanting } from './plants';
import type { MenuState } from './menu';
import { createInitialMenu, getDrink } from './menu';

/**
 * 永続化レイヤの抽象。Web は localStorage、Android は Capacitor Preferences で
 * 実装する。両者とも「文字列キー → 文字列値」の単純な非同期 KVS なので、
 * この最小インターフェースに揃えれば実装を差し替えられる。
 */
export interface KeyValueStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

const SAVE_KEY = 'cometcafe.save';

/** localStorage 実装（Web / dev / Playwright）。同期 API を Promise で包む。 */
export class LocalStorageStore implements KeyValueStore {
  async get(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  async set(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* プライベートモード等で失敗しても進行は止めない */
    }
  }
  async remove(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  }
}

/** テスト用の純メモリ実装。ブラウザ API に依存しない。 */
export class MemoryStore implements KeyValueStore {
  private readonly map = new Map<string, string>();
  async get(key: string): Promise<string | null> {
    return this.map.has(key) ? this.map.get(key)! : null;
  }
  async set(key: string, value: string): Promise<void> {
    this.map.set(key, value);
  }
  async remove(key: string): Promise<void> {
    this.map.delete(key);
  }
}

/** パース直後の生データ（任意フィールド）。型は信用せず検証する。 */
type RawSave = Record<string, unknown>;

/** 値が有限数か。 */
function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/**
 * 任意の値を正規な GardenGrid に整える。長さ不足は null で埋め、
 * 不正なセルは null にする（壊れたセーブでも失敗体験を作らない方針）。
 */
function sanitizeGarden(value: unknown): GardenGrid {
  const grid = createEmptyGarden();
  if (!Array.isArray(value)) return grid;
  const len = Math.min(value.length, GRID_SIZE * GRID_SIZE);
  for (let i = 0; i < len; i++) {
    const cell = value[i] as unknown;
    if (cell == null || typeof cell !== 'object') continue;
    const c = cell as Record<string, unknown>;
    if (typeof c.decoId !== 'string') continue;
    const orientation = c.orientation;
    const o: Orientation =
      orientation === 1 || orientation === 2 || orientation === 3 ? orientation : 0;
    // タイルレベル（v0.7.0）。壊れた・欠落は 1 に丸める。
    const level = clampLevel(isFiniteNumber(c.level) ? c.level : 1);
    grid[i] = { decoId: c.decoId, orientation: o, level } satisfies GardenCell;
  }
  return grid;
}

/**
 * 配置禁止セル（カフェ被り）に置かれているデコを撤去し、コストを返金する。
 * 配置禁止セルの導入（v3）に伴う既存セーブの後始末。返金後の garden と
 * 追加で戻すチップ額を返す（入力は破壊しない）。
 */
function evictBlockedDecos(garden: GardenGrid): { garden: GardenGrid; refund: number } {
  let refund = 0;
  let next: GardenGrid | null = null; // 変更が無ければ作り直さない
  for (const idx of BLOCKED_CELLS) {
    const cell = garden[idx];
    if (cell == null) continue;
    if (next == null) next = garden.slice();
    const deco = getDeco(cell.decoId);
    refund += deco ? deco.cost : 0;
    next[idx] = null;
  }
  return { garden: next ?? garden, refund };
}

/**
 * v6 以前のグリッド一辺（8）。v7 で 12 に拡張したため、旧セーブの再配置に使う。
 */
const OLD_GRID_SIZE = 8;
/** 旧 8×8 を新 12×12 の中央へ寄せるオフセット（(12-8)/2 = 2）。 */
const REGRID_OFFSET = (GRID_SIZE - OLD_GRID_SIZE) / 2;

/**
 * 旧 8×8 のセーブ内容（garden 配列・plantings）を新 12×12 の中央（+2,+2）へ
 * 再配置する（v6→v7 マイグレーション）。
 *
 *  - garden: 旧 index(8×8) → 新 index(12×12) に写像して詰め直す。
 *  - plantings: キー（旧 index の文字列）も新 index に振り直す。
 *  - 範囲・無効セル・カフェ被りの最終整合は呼び出し側の evict/reconcile に委ねる
 *    （旧 8×8 はすべて新 12×12 の中央 8×8 に収まり、半径 4.8 内に入るので無効化は
 *    起きないが、カフェ占有セルの再計算は変わるため後段で必ず evict する）。
 *
 * @param rawGarden 旧セーブの garden（任意の値。長さ・中身は信用しない）。
 * @param rawPlantings 旧セーブの plantings（任意の値）。
 * @returns 新 12×12 の garden（健全化済み）と、新 index に振り直した plantings の生データ。
 */
function regrid8to12(
  rawGarden: unknown,
  rawPlantings: unknown,
): { garden: GardenGrid; plantings: Record<string, unknown> } {
  const garden = createEmptyGarden(); // 新 12×12（全 null）
  // 旧 garden を読み、各セルを新グリッドの中央へ写す。
  if (Array.isArray(rawGarden)) {
    const len = Math.min(rawGarden.length, OLD_GRID_SIZE * OLD_GRID_SIZE);
    for (let i = 0; i < len; i++) {
      const cell = rawGarden[i] as unknown;
      if (cell == null || typeof cell !== 'object') continue;
      const c = cell as Record<string, unknown>;
      if (typeof c.decoId !== 'string') continue;
      const ogx = i % OLD_GRID_SIZE;
      const ogy = Math.floor(i / OLD_GRID_SIZE);
      const ngx = ogx + REGRID_OFFSET;
      const ngy = ogy + REGRID_OFFSET;
      const orientation = c.orientation;
      const o: Orientation =
        orientation === 1 || orientation === 2 || orientation === 3 ? orientation : 0;
      const level = clampLevel(isFiniteNumber(c.level) ? c.level : 1);
      garden[ngy * GRID_SIZE + ngx] = { decoId: c.decoId, orientation: o, level };
    }
  }
  // plantings のキー（旧 index）を新 index に振り直す。
  const plantings: Record<string, unknown> = {};
  if (rawPlantings != null && typeof rawPlantings === 'object') {
    for (const [key, val] of Object.entries(rawPlantings as Record<string, unknown>)) {
      const oldIdx = Number(key);
      if (!Number.isInteger(oldIdx) || oldIdx < 0 || oldIdx >= OLD_GRID_SIZE * OLD_GRID_SIZE) continue;
      const ogx = oldIdx % OLD_GRID_SIZE;
      const ogy = Math.floor(oldIdx / OLD_GRID_SIZE);
      const newIdx = (ogy + REGRID_OFFSET) * GRID_SIZE + (ogx + REGRID_OFFSET);
      plantings[String(newIdx)] = val;
    }
  }
  return { garden, plantings };
}

/**
 * 無効セル（島の円形上面の外）に置かれたデコを撤去し、コストを返金する。
 * グリッド拡張（v7）で角のセルが無効になったことの後始末。evictBlockedDecos と
 * 同じ作法で、変更が無ければ配列を作り直さない。
 */
function evictInvalidCells(garden: GardenGrid): { garden: GardenGrid; refund: number } {
  let refund = 0;
  let next: GardenGrid | null = null;
  for (let idx = 0; idx < garden.length; idx++) {
    const cell = garden[idx];
    if (cell == null) continue;
    const gx = idx % GRID_SIZE;
    const gy = Math.floor(idx / GRID_SIZE);
    if (isValidCell(gx, gy)) continue; // 有効セルはそのまま
    if (next == null) next = garden.slice();
    const deco = getDeco(cell.decoId);
    refund += deco ? deco.cost : 0;
    next[idx] = null;
  }
  return { garden: next ?? garden, refund };
}

/**
 * 形状 shape の外（その島の上面に乗らないセル）に置かれたデコを撤去し、投入総額を
 * 全額返金する（v0.7.3）。拡張島の形状バリアント導入の後始末。round 形状なら
 * evictInvalidCells と同じ円外 evict になる。変更が無ければ配列を作り直さない。
 *
 * 返金は totalInvested（base + 各 level のアップグレード）で全額返す（円外退避と同方針）。
 */
function evictOutsideShape(garden: GardenGrid, shape: IslandShape): { garden: GardenGrid; refund: number } {
  let refund = 0;
  let next: GardenGrid | null = null;
  for (let idx = 0; idx < garden.length; idx++) {
    const cell = garden[idx];
    if (cell == null) continue;
    const gx = idx % GRID_SIZE;
    const gy = Math.floor(idx / GRID_SIZE);
    if (isCellInShape(gx, gy, shape)) continue; // 形状内はそのまま
    if (next == null) next = garden.slice();
    const deco = getDeco(cell.decoId);
    refund += deco ? totalInvested(cell.decoId, clampLevel(cell.level ?? 1)) : 0;
    next[idx] = null;
  }
  return { garden: next ?? garden, refund };
}

/**
 * 入口ゲートの固定セルに（入口でない）デコが置かれていたら撤去して返金する（v0.7.0）。
 * 入口セルは固定なので、旧セーブで偶然ここにデコがあった場合は退かして全額返す。
 * 退かしたあと ensureEntrance が入口を置く。入力は破壊しない。
 */
function evictEntranceCell(garden: GardenGrid): { garden: GardenGrid; refund: number } {
  const cell = garden[ENTRANCE_INDEX];
  if (cell == null || cell.decoId === ENTRANCE_DECO_ID) return { garden, refund: 0 };
  const next = garden.slice();
  const deco = getDeco(cell.decoId);
  const level = clampLevel(cell.level ?? 1);
  const refund = deco ? totalInvested(cell.decoId, level) : 0;
  next[ENTRANCE_INDEX] = null;
  return { garden: next, refund };
}

/** 任意の値を 32bit seed に整える（不正なら初期 seed）。 */
function sanitizeSeed(value: unknown): number {
  return isFiniteNumber(value) ? value >>> 0 : INITIAL_SEED;
}

/** 任意の値を [0,1] にクランプした数に整える（不正なら既定値）。 */
function num01(value: unknown, fallback: number): number {
  if (!isFiniteNumber(value)) return fallback;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** 任意の値を樹形に整える（壊れたフィールドは中庸値・ニュートラルに補正）。 */
function sanitizeForm(value: unknown): TreeForm {
  const o = (value != null && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const trained = isFiniteNumber(o.trained) ? Math.max(-1, Math.min(1, o.trained)) : 0;
  return {
    branches: num01(o.branches, 0.5),
    bend: num01(o.bend, 0.5),
    lush: num01(o.lush, 0.5),
    trained,
  };
}

/**
 * 任意の値を plantings（セル index → 生育状態）に整える。キーが数値文字列で、
 * 値が妥当なエントリだけ取り込む。growSec は負を 0 に丸める（枯れ・巻き戻し無し）。
 */
function sanitizePlantings(value: unknown): Record<string, Planting> {
  const out: Record<string, Planting> = {};
  if (value == null || typeof value !== 'object') return out;
  const raw = value as Record<string, unknown>;
  for (const [key, e] of Object.entries(raw)) {
    const idx = Number(key);
    if (!Number.isInteger(idx) || idx < 0 || idx >= GRID_SIZE * GRID_SIZE) continue;
    if (e == null || typeof e !== 'object') continue;
    const o = e as Record<string, unknown>;
    const growSec = isFiniteNumber(o.growSec) && o.growSec > 0 ? o.growSec : 0;
    out[String(idx)] = { growSec, form: sanitizeForm(o.form) };
  }
  return out;
}

/**
 * 庭の植物デコと plantings を突き合わせて整合させる（入力は破壊しない）。
 *
 *  - 植物デコが置かれているのに plantings が無いセル（v3 以前のセーブの既存の若木や、
 *    壊れたデータ）には、芽から始まる生育状態を補う（seed・セル index から決定的に
 *    樹形を導出）。
 *  - 植物デコが無いセルの plantings は捨てる（撤去済み・不整合の掃除）。
 */
function reconcilePlantings(
  garden: GardenGrid,
  plantings: Record<string, Planting>,
  seed: number,
): Record<string, Planting> {
  const out: Record<string, Planting> = {};
  for (let idx = 0; idx < garden.length; idx++) {
    const cell = garden[idx];
    if (!cell || !isPlantDeco(cell.decoId)) continue;
    const existing = plantings[String(idx)];
    out[String(idx)] = existing ?? createPlanting(seed, cell.decoId, idx);
  }
  return out;
}

/**
 * 健全化済みの garden と plantings 生データから「ホーム島（index 0）」を組み立てる（v0.7.1）。
 *
 * ホーム島はカフェ・入口がある唯一の島なので、円外（無効セル）→ カフェ被り → 入口セルの
 * 順に evict（返金）し、入口ゲートを必ず 1 つ置く。植物は庭と整合させる。返金額も返す。
 */
function buildHomeIsland(
  rawGarden: GardenGrid,
  rawPlantings: Record<string, Planting>,
  seed: number,
): { island: Island; refund: number } {
  const evictInvalid = evictInvalidCells(rawGarden);
  const evictBlocked = evictBlockedDecos(evictInvalid.garden);
  const evictEntrance = evictEntranceCell(evictBlocked.garden);
  const garden = ensureEntrance(evictEntrance.garden);
  const refund = evictInvalid.refund + evictBlocked.refund + evictEntrance.refund;
  const plantings = reconcilePlantings(garden, rawPlantings, seed);
  return { island: { garden, plantings }, refund };
}

/**
 * 拡張島（index ≥1）を組み立てる（v0.7.1、v0.7.3 で形状対応）。カフェも入口も無いので、
 * **その島の形状の外**に出たデコだけを evict（全額返金）する（カフェ被り {4,5}×{4,5} は
 * 形状内なら普通に置けるので evict しない・入口も無い）。植物は庭と整合させる。返金額も返す。
 *
 * 形状は islandIndex から shapeForIslandIndex で決定的に決まる（v10→v11 移行で、既存の
 * 拡張島を各形状の述語で reconcile する）。round 形状なら従来の円外 evict と同値。
 */
function buildExtensionIsland(
  rawGarden: GardenGrid,
  rawPlantings: Record<string, Planting>,
  seed: number,
  islandIndex: number,
): { island: Island; refund: number } {
  const shape = shapeForIslandIndex(islandIndex);
  const evicted = evictOutsideShape(rawGarden, shape); // 形状外を全額返金して撤去
  const garden = evicted.garden;
  const plantings = reconcilePlantings(garden, rawPlantings, seed);
  return { island: { garden, plantings }, refund: evicted.refund };
}

/**
 * 任意の値をカフェメニュー状態に整える。developed は既知のドリンク ID だけを
 * 重複なく取り込み、special は developed に含まれる既知 ID のときだけ採用する
 * （不変条件「special ⊆ developed」を保証する）。
 */
function sanitizeMenu(value: unknown): MenuState {
  if (value == null || typeof value !== 'object') return createInitialMenu();
  const o = value as Record<string, unknown>;
  const developed: string[] = [];
  const seen = new Set<string>();
  if (Array.isArray(o.developed)) {
    for (const id of o.developed) {
      if (typeof id !== 'string' || seen.has(id)) continue;
      if (!getDrink(id)) continue; // 未知のドリンクは捨てる
      seen.add(id);
      developed.push(id);
    }
  }
  let special: string | null = null;
  if (typeof o.special === 'string' && seen.has(o.special)) special = o.special;
  return { developed, special };
}

/** 任意の値を訪問ログ配列に整える（不正エントリは捨て、上限で丸める）。 */
function sanitizeVisitLog(value: unknown): VisitLogEntry[] {
  if (!Array.isArray(value)) return [];
  const out: VisitLogEntry[] = [];
  for (const e of value) {
    if (e == null || typeof e !== 'object') continue;
    const o = e as Record<string, unknown>;
    if (typeof o.customerId !== 'string') continue;
    if (!isFiniteNumber(o.atMs) || !isFiniteNumber(o.tip)) continue;
    out.push({ customerId: o.customerId, atMs: o.atMs, tip: o.tip });
  }
  return out.length > VISIT_LOG_LIMIT ? out.slice(out.length - VISIT_LOG_LIMIT) : out;
}

/**
 * 任意の値を図鑑に整える。既知の全客種ぶんのエントリを必ず持たせ（新客種の
 * 追加にも追従）、セーブにある既知 ID の値だけ取り込む。
 */
function sanitizeDex(value: unknown): Dex {
  const dex = createEmptyDex();
  if (value == null || typeof value !== 'object') return dex;
  const raw = value as Record<string, unknown>;
  for (const c of CUSTOMER_CATALOG) {
    const e = raw[c.id];
    if (e == null || typeof e !== 'object') continue;
    const o = e as Record<string, unknown>;
    const count = isFiniteNumber(o.count) && o.count >= 0 ? Math.floor(o.count) : 0;
    const discovered = o.discovered === true || count > 0;
    const firstAtMs = isFiniteNumber(o.firstAtMs) ? o.firstAtMs : null;
    dex[c.id] = { discovered, count, firstAtMs: discovered ? firstAtMs : null };
  }
  return dex;
}

/**
 * 生セーブデータを現行スキーマ（SAVE_VERSION）の GameState にマイグレーションする。
 * 妥当でなければ null を返す（呼び出し側が初期状態にフォールバックする）。
 *
 * - v1 → : garden が無いので空の庭を補う。
 * - v2 → : 客システム（seed / visitLog / dex / visitAccumSec）が無いので初期値を補う。
 * - v3 → : 盆栽の plantings が無いので、置かれている植物デコに芽から始まる生育状態を補う。
 * - v4 → : カフェメニュー menu が無いので、空のメニュー（未開発・スペシャル未選択）を補う。
 * - v5 → : ミュート設定 muted が無いので false（音あり）を補う。
 * - v6 → : グリッドを 8×8 → 12×12 に拡張。旧庭を中央（+2,+2）へ再配置し、無効セル
 *   （島の円外）・カフェ被りに落ちたデコは返金撤去。評判の累計来店数 totalVisits を補う。
 * - v7 → : v0.6.0（ゆらぎ・昼夜・季節・シナジー）。新規の永続フィールドは無いので、
 *   バージョンを上げて引き継ぐだけ（新挙動は seed＋絶対時刻からの導出で、移行不要）。
 * - v8 → : v0.7.0。各セルに level（1..5）を補完（sanitize/regrid で欠落は 1）。入口ゲートを
 *   島手前の固定セルに必ず 1 つ配置（占有していたデコは退かして全額返金）。
 * - v10 → v11: v0.7.3。島ごとの形状バリアント。形状は island index 導出（永続フィールド無し）。
 *   既存の拡張島(≥1)を各形状の述語で reconcile し、形状外に出たデコは全額返金して撤去する
 *   （ホーム島(0)は round のまま不変）。実装は buildExtensionIsland の evictOutsideShape。
 * - v11 → v12: v0.7.4。形状ラインアップ更新（island1: oval→square / island2: hex→twin）。同じく
 *   index 導出（永続フィールド無し）。buildExtensionIsland が現行 shapeForIslandIndex（新ライン
 *   アップ）で evictOutsideShape を再適用するため、旧形状で有効だが新形状の外に出たデコは自動で
 *   全額返金・撤去される（v10/v11 と同じ経路。バージョンを上げるだけで新形状が効く）。
 * - 全バージョン共通: カフェ被りセル・無効セル・入口セルに置かれたデコは撤去し全額返金する。
 *   植物デコと plantings の整合（足りない分の補完・余分の掃除）も行う。
 */
function migrate(raw: RawSave, nowMs: number): GameState | null {
  const version = raw.version;
  if (typeof version !== 'number' || version > SAVE_VERSION) return null;
  if (!isFiniteNumber(raw.chips) || !isFiniteNumber(raw.chipRate) || !isFiniteNumber(raw.lastTickMs)) {
    return null;
  }

  // 客システム（v3 で追加）。v2 以前は seed/visitLog/dex を持たないので初期化。
  // seed は島の植物の樹形補完（createPlanting）に要るので、島の組み立て前に確定する。
  const seed = version < 3 ? INITIAL_SEED : sanitizeSeed(raw.seed);
  const visitLog = version < 3 ? [] : sanitizeVisitLog(raw.visitLog);
  const dex = version < 3 ? createEmptyDex() : sanitizeDex(raw.dex);
  const visitAccumSec =
    version < 3 || !isFiniteNumber(raw.visitAccumSec) ? 0 : Math.max(0, raw.visitAccumSec);

  // --- 島の正規化（v0.7.1 多島化） ---
  // v10 未満は単一島（トップレベル garden/plantings）→ ホーム島（index 0）に包む。
  // v6 以前は 8×8 を 12×12 中央へ regrid、v1 は garden 無し → 空の庭。
  // ホーム島だけ入口/カフェ被りを evict（拡張島はそれらが無いので円外だけ evict）。
  let islands: Island[];
  let unlockedIslands: number;
  let refund = 0;

  if (version < 10) {
    // 旧単一島セーブ。garden/plantings を健全化してホーム島に包む。
    let sanitized: GardenGrid;
    let rawPlantingsValue: unknown;
    if (version < 2) {
      sanitized = createEmptyGarden();
      rawPlantingsValue = {};
    } else if (version < 7) {
      const regridded = regrid8to12(raw.garden, version < 4 ? {} : raw.plantings);
      sanitized = regridded.garden;
      rawPlantingsValue = regridded.plantings;
    } else {
      sanitized = sanitizeGarden(raw.garden);
      rawPlantingsValue = raw.plantings;
    }
    const rawPlantings = version < 4 ? {} : sanitizePlantings(rawPlantingsValue);
    const home = buildHomeIsland(sanitized, rawPlantings, seed);
    islands = [home.island];
    unlockedIslands = 1;
    refund += home.refund;
  } else {
    // v10 新形式: islands 配列・unlockedIslands を読む。
    const rawIslands = Array.isArray(raw.islands) ? raw.islands : [];
    // 健全化のため最低 1 島（ホーム）は必ず作る。
    islands = [];
    const count = Math.max(1, rawIslands.length);
    for (let i = 0; i < count; i++) {
      const rawIsland = (rawIslands[i] ?? {}) as Record<string, unknown>;
      const grid = sanitizeGarden(rawIsland.garden);
      const rawPlantings = sanitizePlantings(rawIsland.plantings);
      const built =
        i === 0
          ? buildHomeIsland(grid, rawPlantings, seed)
          : buildExtensionIsland(grid, rawPlantings, seed, i);
      islands.push(built.island);
      refund += built.refund;
    }
    // unlockedIslands は [1, MAX_ISLANDS] にクランプ。islands.length とのずれは
    // 「データ消失を避ける」方針で islands.length を優先して合わせる（島が多ければ
    // unlockedIslands を上げ、宣言が多すぎれば空島で埋め、超過した島は切る）。
    let want = isFiniteNumber(raw.unlockedIslands)
      ? Math.max(1, Math.min(MAX_ISLANDS, Math.floor(raw.unlockedIslands)))
      : islands.length;
    // 実体島が宣言より多い → データを残すため unlockedIslands を島数に合わせて上げる。
    if (islands.length > want) want = Math.min(MAX_ISLANDS, islands.length);
    // 宣言が実体より多い → 足りない拡張島を空島で埋める。
    while (islands.length < want) islands.push(buildExtensionIsland(createEmptyGarden(), {}, seed, islands.length).island);
    // 上限・宣言を超えた島は切る（MAX_ISLANDS まで）。
    if (islands.length > want) islands = islands.slice(0, want);
    unlockedIslands = want;
  }

  // カフェメニュー（v5 で追加）。v4 以前は menu を持たないので空メニューを補う。
  const menu = version < 5 ? createInitialMenu() : sanitizeMenu(raw.menu);

  // サウンドのミュート設定（v6 で追加）。v5 以前は muted を持たないので既定 false。
  // 真偽値以外（壊れた値）も false に倒す。
  const muted = version < 6 ? false : raw.muted === true;

  // 評判の累計来店数（v7 で追加）。v6 以前は持たないので、図鑑の累計から推定して補う
  // （来店履歴は dex.count に残っているので、その総和を初期評判の土台にする）。
  let totalVisits: number;
  if (version < 7 || !isFiniteNumber(raw.totalVisits)) {
    totalVisits = Object.values(dex).reduce((sum, e) => sum + (e.count || 0), 0);
  } else {
    totalVisits = Math.max(0, Math.floor(raw.totalVisits));
  }

  return {
    version: SAVE_VERSION,
    chips: raw.chips + refund,
    chipRate: raw.chipRate,
    lastTickMs: raw.lastTickMs,
    islands,
    unlockedIslands,
    seed,
    visitLog,
    dex,
    visitAccumSec,
    menu,
    muted,
    totalVisits,
  };
  // nowMs はこの関数では使わない（lastTickMs はセーブ値を尊重し、
  // オフライン精算は settleOffline に委ねる）。引数は将来の移行で時刻補正に使える。
  void nowMs;
}

/**
 * セーブデータをロードする。未保存・壊れている・将来バージョンの場合は
 * 新規状態を返す（失敗体験を作らない方針なので例外を投げない）。
 * 旧バージョンのセーブはマイグレーションして引き継ぐ。
 */
export async function loadGame(store: KeyValueStore, nowMs: number): Promise<GameState> {
  const raw = await store.get(SAVE_KEY);
  if (raw == null) return createInitialState(nowMs);
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed == null || typeof parsed !== 'object') return createInitialState(nowMs);
    const migrated = migrate(parsed as RawSave, nowMs);
    return migrated ?? createInitialState(nowMs);
  } catch {
    return createInitialState(nowMs);
  }
}

/** セーブデータを保存する。 */
export async function saveGame(store: KeyValueStore, state: GameState): Promise<void> {
  await store.set(SAVE_KEY, JSON.stringify(state));
}

export { SAVE_KEY };
